/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSessionStore } from '@insta-club/state';
import { getCookie, removeCookie } from '@insta-club/utilities';
import { stringify } from 'querystring';
import toast from 'react-hot-toast';

type LocationPath = 'dashboard' | 'web';

export interface FetcherOptions {
    headers?: Record<string, string>;
    params?: Record<string, any>;
    body?: any;
    requiredAuth?: boolean;
    forceBearer?: string;
}

type method = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';

const getFetchPath = (location: LocationPath) => {
    switch (location) {
        case 'dashboard':
        case 'web':
            return process.env.NEXT_PUBLIC_API_URL;
        default:
            return '';
    }
};

const fetcher = async (
    fetcherKey: string,
    location: LocationPath,
    method: method = 'GET',
    options?: FetcherOptions
) => {
    let requiredAuth = true;

    if ((options && options.requiredAuth === false) || location !== 'dashboard') {
        requiredAuth = false;
    }

    const isFormData = options?.body instanceof FormData;

    const headersList: HeadersInit = {
        Accept: 'application/json',
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(options?.headers || {})
    };

    if (requiredAuth) {
        const access_token = await getCookie('insta-club-session');
        if (!access_token || access_token.length < 5) {
            removeCookie('insta-club-session');
            if (typeof window !== 'undefined') {
                toast.error('دسترسی غیر مجاز');
                window.location.href = '/auth/sign-in';
            }
            return;
        }
        headersList.Authorization = `Bearer ${access_token}`;
    }

    const activePageId = typeof window !== 'undefined' ? useSessionStore.getState().activePageId : undefined;

    const queryParams = {
        ...(options?.params || {}),
        ...(method === 'GET' && options?.params?.page_id === undefined && activePageId ? { page_id: activePageId } : {})
    };

    if ((queryParams.page_id as any) === false) {
        delete queryParams.page_id;
    }

    let bodyData: any;
    if (method !== 'GET') {
        if (isFormData) {
            bodyData = options?.body;
        } else {
            bodyData = JSON.stringify({
                ...(options?.body || {}),
                ...(activePageId ? { page_id: activePageId } : {})
            });
        }
    }

    const requestInit: RequestInit = {
        method,
        headers: headersList,
        ...(bodyData && { body: bodyData })
    };

    try {
        const constructUrl = [getFetchPath(location), fetcherKey];
        if (Object.keys(queryParams).length > 0) {
            constructUrl.push(`?${stringify(queryParams)}`);
        }

        console.log(`${method} -> ${location} -> ${requiredAuth ? '🔒' : ''} ${constructUrl.join('')} `);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

        try {
            const response = await fetch(constructUrl.join(''), {
                ...requestInit,
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw { status: response.status, error };
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            } else {
                return await response.blob();
            }
        } catch (fetchError: any) {
            clearTimeout(timeoutId);

            if (fetchError.name === 'AbortError') {
                throw {
                    status: 408,
                    error: { message: 'درخواست به دلیل طولانی شدن زمان لغو شد' }
                };
            }
            throw fetchError;
        }
    } catch (err: any) {
        if (err.status === 401 && typeof window !== 'undefined') {
            try {
                toast.error('دسترسی غیر مجاز، مجدد وارد شوید!', { toasterId: 'logout-401' });
            } catch (toastErr) {
                console.error('Toast error:', toastErr);
            }
            removeCookie('insta-club-session');
            window.location.href = '/auth/sign-in';
            return;
        }
        if (err.status === 408 && typeof window !== 'undefined') {
            try {
                toast.error('درخواست به دلیل طولانی شدن زمان لغو شد. لطفا اتصال اینترنت خود را بررسی کنید.', {
                    toasterId: 'request-timeout'
                });
            } catch (toastErr) {
                console.error('Toast error:', toastErr);
            }
        }
        if (err.status === 422) throw err.error;
        throw err;
    }
};

export default fetcher;
